import sum from 'lodash/sum';
import moment from 'moment';

import { PersonRelationShipType } from '@appTypes/enums';
import type { RenewalRelationship } from '@appTypes/esb';
import { OrgAdvancementsDashboard } from '@appTypes/esb/organizations/v2/_orgGuid/advancementDashboard';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { Program } from '@shared/constants';

import { ExpirationStatus } from './enums';

const EXP_MODALS_KEY = 'expirationModals';
const RENEWAL_DAYS_THRESHOLD = 60;

type MembershipModalMeta = {
  personGuid: string;
  organizationGuid: string;
  registrationId: number;
};

function getMembershipModalKey(params: MembershipModalMeta) {
  return `${params.personGuid}_${params.organizationGuid}_${params.registrationId}`;
}

export function storeMembershipExpirationDismissFlag(params: {
  personGuid: string;
  organizationGuid: string;
  registrationId: number;
}) {
  try {
    const data = JSON.parse(localStorage.getItem(EXP_MODALS_KEY) || '{}');
    const key = getMembershipModalKey(params);
    const oldData: MembershipModalMeta = data[key] || {};
    data[key] = { ...oldData, dismissed: new Date().toISOString() };
    localStorage.setItem(EXP_MODALS_KEY, JSON.stringify(data));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('could not save state', error);
  }
}

export function getMembershipExpirationDismissFlag(
  params: MembershipModalMeta,
): Record<string, { dismissed: string }> | null {
  try {
    const data = JSON.parse(localStorage.getItem(EXP_MODALS_KEY) || '{}') || {};
    const key = getMembershipModalKey(params);
    return data[key] || null;
  } catch {
    return null;
  }
}

export function getExpirationStatus(relationship: RenewalRelationship) {
  const expirationDate = moment(relationship.personExpiryDt);

  const daysLeft = moment(expirationDate).diff(moment(), 'days');

  const expiringSoon =
    daysLeft > 0 &&
    daysLeft < RENEWAL_DAYS_THRESHOLD &&
    !relationship.orderId &&
    !relationship.isAutoRenewalOptedOut &&
    !relationship.isManuallyEnded &&
    !relationship.councilPaid;

  const expired = daysLeft < 0;
  const longExpired =
    daysLeft < 0 && Math.abs(daysLeft) > RENEWAL_DAYS_THRESHOLD;

  if (expiringSoon) {
    return ExpirationStatus.ABOUT_TO_EXPIRE;
  } else if (expired) {
    return ExpirationStatus.EXPIRED;
  } else if (longExpired) {
    return ExpirationStatus.LONG_EXPIRED;
  } else {
    return ExpirationStatus.UNKNOWN;
  }
}

const parentRelationship = new Set([
  PersonRelationShipType.FatherOf,
  PersonRelationShipType.MotherOf,
]);

export function isParentRelationship(renewal: RenewalRelationship) {
  if (!renewal) return false;

  return !!(
    renewal.relationshipTypeId &&
    parentRelationship.has(renewal.relationshipTypeId)
  );
}

export function computeNotificationsCount(params: {
  stats?: OrgAdvancementsDashboard;
  unitProgram: Program;
  positionsCount: number;
}) {
  const { stats, positionsCount, unitProgram } = params;
  const toPurchaseCount = sum(Object.values(stats?.notPurchased || {})) || 0;
  const requirementsCount = featureFlags.getFlag(
    'SBL_4724_PENDING_REQUIREMENTS',
  )
    ? sum([
        stats?.completed?.requirements?.ranks,
        stats?.completed?.requirements?.awards,
        stats?.completed?.requirements?.adventures,
        unitProgram !== Program.CUB_SCOUT
          ? stats?.completed?.requirements?.meritBadges
          : 0,
      ])
    : 0;

  const toApproveCount = sum([
    unitProgram === Program.CUB_SCOUT ? stats?.completed?.adventures : 0,
    stats?.completed?.awards,
    stats?.completed?.ranks,
    unitProgram === Program.CUB_SCOUT ? 0 : stats?.completed?.meritBadges,
    positionsCount,
    requirementsCount,
  ]);
  const totalCount = toApproveCount + toPurchaseCount;
  return {
    totalCount,
    toApproveCount,
    toPurchaseCount,
    positionsCount,
  };
}

export function removeRenewedRegistrations(
  renewalRegistrations: RenewalRelationship[] = [],
) {
  const activeRegistrations = renewalRegistrations.filter(reg => {
    const daysLeft = moment(reg.personExpiryDt).diff(moment(), 'days');
    return daysLeft > RENEWAL_DAYS_THRESHOLD;
  });
  const filteredRenewals = renewalRegistrations.filter(renewals => {
    const samePosition = activeRegistrations.find(
      reg =>
        reg.memberId === renewals.memberId &&
        reg.positionId === renewals.positionId &&
        reg.organizationGuid === renewals.organizationGuid,
    );
    return !samePosition;
  });
  return filteredRenewals;
}

/**
 * Applying same logic as MyST
 *
 */
export function filterRenewalNotifications(
  renewals: RenewalRelationship[] = [],
) {
  return removeRenewedRegistrations(renewals).filter(rel => {
    const daysLeft = moment(rel.personExpiryDt).diff(moment(), 'days');
    return (
      daysLeft < RENEWAL_DAYS_THRESHOLD &&
      !rel.orderId &&
      !rel.isAutoRenewalOptedOut &&
      !rel.isManuallyEnded &&
      !rel.councilPaid
    );
  });
}
